/*
 * The film stops growing with its column, and with the window.
 *
 * Width first: a day cell sized by dividing a desktop column by seven is a
 * 90px square where the phone has 44, and a film given the whole column is
 * the same drawing at twice the size — a different component rather than
 * the same one on a bigger screen. Then height, which the width bound alone
 * never held: the canon is 2:3, so a 24rem film is 36rem tall, and in a
 * 720px window — an ordinary laptop's, with the browser's own strip taken
 * off — the scrub under it sat below the fold on the one screen whose
 * whole job is to be scrubbed. On the public page the width bound was not
 * even there: it lived in the diary's stylesheet, which that page never
 * loads, and a shared film was 528px wide and its scrub a thousand pixels
 * down.
 *
 * So the width also follows the room the window has for a 2:3 frame once
 * what stands above and below it is taken off — `--film-room`, set by the
 * page: the public page's header and scrub; in the app the masthead too,
 * and on a phone the chrome floating over the bottom of the page, which
 * on a wide window is a rail at the side and takes no height. A floor
 * keeps a phone turned sideways from shrinking the film to a stamp; there
 * the scrub is a short scroll away.
 *
 * The figure is bounded and not only the frame inside it, because the scrub
 * is the figure's other child. Bound the frame alone and the two part
 * company the moment the height bound bites: in a 1280×800 window the film
 * came out 384px wide and the play button, the rail and the date went on to
 * 528 — the control sticking out a hundred and forty pixels past the thing
 * it controls, on the laptop the film screen is most often opened on. The
 * frame keeps its own copy of the rule for the one place it stands without
 * a figure around it: the placeholder the diary draws while the film's
 * answer is on its way, which has to be exactly the size of the frame it
 * resolves into or the shelf under it moves.
 */
.timelapse-frame,
.timelapse-figure {
  --film-room: 14rem;
  max-width: min(24rem, max(10rem, calc((100dvh - var(--film-room)) * 2 / 3)));
}

.shell-wide .timelapse-frame,
.shell-wide .timelapse-figure {
  --film-room: 15rem;
}

.shell-pager .timelapse-frame,
.shell-pager .timelapse-figure {
  --film-room: 20rem;
}

/*
 * The gallery a reader without scripting gets — and only them.
 *
 * The frames past the first are sent inside a `<noscript>`, so a browser
 * with scripting on makes no elements out of them and fetches nothing (see
 * TimelapseFilm). That element wants `display: contents` when it IS
 * rendered, so each frame becomes a row of the figure with the same gap as
 * the first rather than one inline lump.
 *
 * It was a plain class, and `display` is exactly what must not be said
 * unconditionally here: with scripting ON the standard's own stylesheet
 * gives `noscript` `display: none`, the contents are parsed as ONE TEXT
 * NODE, and `contents` overrode the none and laid that text out. A stranger
 * opening a shared album read five lines of `<img src="…" alt="The day of
 * August 2, 2026" class="…"/>` under the photograph until hydration cleared
 * it — and the page jumped 128px when it did, 0.113 of the viewport.
 *
 * So the override is scoped to the only case that wants it. A browser too
 * old for the feature keeps the standard's behaviour both ways round: no
 * text with scripting on, and an ungapped stack without it.
 */
@media (scripting: none) {
  .timelapse-gallery {
    display: contents;
  }
}
